import { useEffect, useRef, useState } from "react";

export type SelectOption = { value: string; label: string };
export type SelectProps = {
  value?: string;
  onChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  label?: string;
  size?: "default" | "sm";
};

const Chevron = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const Check = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/** Lightweight controlled select with the PRDS pop-in menu. Closes on outside click / Escape. */
export function Select({
  value,
  onChange,
  options,
  placeholder = "Select…",
  className = "",
  label,
  size = "default",
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const selected = options.find((o) => o.value === value);
  const control = (
    <div className={["pr-select", className].filter(Boolean).join(" ")} ref={ref}>
      <button
        type="button"
        className="pr-select__trigger"
        data-placeholder={!selected}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={size === "sm" ? { height: "2.25rem" } : undefined}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </span>
        <span data-open={open} className="pr-select__chev">
          <Chevron />
        </span>
      </button>
      {open && (
        <div className="pr-select__menu" role="listbox">
          {options.map((o, i) => (
            <div
              key={o.value}
              className="pr-select__item"
              role="option"
              tabIndex={0}
              aria-selected={o.value === value}
              data-selected={o.value === value}
              style={{ "--pr-item-delay": `${i * 30}ms` } as React.CSSProperties}
              onClick={() => {
                onChange?.(o.value);
                setOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onChange?.(o.value);
                  setOpen(false);
                }
              }}
            >
              <span>{o.label}</span>
              <span className="pr-select__check" aria-hidden="true">
                <Check />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  if (!label) return control;
  return (
    <div className="pr-field">
      <span className="pr-label">{label}</span>
      {control}
    </div>
  );
}
